import { api } from '@/lib/api';
import { getAllPages } from '@/lib/paginated-data';
import { probeMedia } from '@/lib/media-probe';

export type SocialPlatform =
  | 'linkedin'
  | 'facebook'
  | 'instagram'
  | 'youtube'
  | 'tiktok'
  | 'reddit'
  | 'x'
  | 'threads'
  | 'pinterest'
  | 'google_business';

export type SocialPostKind =
  | 'feed'
  | 'story'
  | 'reel'
  | 'short'
  | 'carousel'
  | 'video'
  | 'text'
  | 'link';

export type SocialTargetStatus =
  | 'draft'
  /** Wartet auf Freigabe (Einstellung „Freigabe erforderlich“). */
  | 'pending_approval'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled';

/** Board-Spalten des Content-Planners. */
export type BoardStatus =
  | 'idea'
  | 'draft'
  | 'review'
  | 'approved'
  | 'scheduled'
  | 'published'
  | 'archived';

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  external_account_id: string;
  display_name: string;
  avatar_url?: string | null;
  status: 'active' | 'expired' | 'revoked' | 'error';
  ig_user_id?: string | null;
  facebook_page_id?: string | null;
  youtube_channel_id?: string | null;
  tiktok_open_id?: string | null;
  reddit_username?: string | null;
  x_user_id?: string | null;
  threads_user_id?: string | null;
  pinterest_board_id?: string | null;
  google_location_id?: string | null;
  token_expires_at?: string | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

/** Kanal-spezifische Extras eines Ziels. */
export interface SocialTargetMetadata {
  subreddit?: string;
  flair_id?: string;
  flair_text?: string;
  nsfw?: boolean;
  spoiler?: boolean;
  send_replies?: boolean;
  disable_comment?: boolean;
  disable_duet?: boolean;
  disable_stitch?: boolean;
  auto_add_music?: boolean;
  video_cover_timestamp_ms?: number;
  /** Pinterest: Ziel-Board. */
  board_id?: string;
  /** Pinterest: Ziel-Link des Pins. */
  destination_url?: string;
  /** Google Business Profile. */
  language_code?: string;
  cta_type?: 'BOOK' | 'ORDER' | 'SHOP' | 'LEARN_MORE' | 'SIGN_UP' | 'CALL';
}

export interface SocialPostTarget {
  id: string;
  post_id: string;
  social_account_id: string;
  platform: SocialPlatform;
  post_kind: SocialPostKind;
  status: SocialTargetStatus;
  scheduled_at?: string | null;
  published_at?: string | null;
  external_post_id?: string | null;
  external_url?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  adapted_body?: string | null;
  adapted_title?: string | null;
  privacy_status?: string | null;
  metadata?: SocialTargetMetadata;
  analytics?: Record<string, unknown>;
  attempt_count?: number;
  social_accounts?: Pick<SocialAccount, 'id' | 'platform' | 'display_name' | 'avatar_url' | 'status'>;
}

export interface ContentPillar {
  id: string;
  name: string;
  description?: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
}

export interface ContentSlot {
  id: string;
  label: string;
  /** 1 = Montag … 7 = Sonntag */
  weekday: number;
  time_of_day: string;
  platform?: SocialPlatform | null;
  pillar_id?: string | null;
  post_kind?: SocialPostKind | null;
  is_active: boolean;
}

export interface SocialPost {
  id: string;
  title?: string | null;
  body?: string | null;
  post_kind: SocialPostKind;
  link_url?: string | null;
  first_comment?: string | null;
  campaign_id?: string | null;
  status?: string;
  created_at?: string;
  /** Planner-Felder */
  updated_at?: string;
  planned_at?: string | null;
  board_status?: BoardStatus;
  pillar_id?: string | null;
  pillar?: Pick<ContentPillar, 'id' | 'name' | 'color'> | null;
  owner_id?: string | null;
  notes?: string | null;
  tags?: string[];
  approved_at?: string | null;
  newsletter_enabled?: boolean;
  newsletter_list_id?: string | null;
  newsletter_subject?: string | null;
  newsletter_from_name?: string | null;
  newsletter_reply_to?: string | null;
  newsletter_campaign_id?: string | null;
  newsletter_status?: string | null;
  newsletter_error?: string | null;
  targets: SocialPostTarget[];
  media?: MediaAsset[];
}

export interface MediaAsset {
  id: string;
  public_url?: string | null;
  mime_type: string;
  byte_size?: number;
  original_filename?: string | null;
  width?: number | null;
  height?: number | null;
  duration_seconds?: number | null;
  created_at?: string;
}

export interface PlannerData {
  from: string;
  to: string;
  planned: SocialPost[];
  backlog: SocialPost[];
  pillars: ContentPillar[];
  slots: ContentSlot[];
  stats: Partial<Record<BoardStatus, number>>;
}

export interface CommandCenterData {
  accounts: Array<{
    id: string;
    platform: SocialPlatform;
    display_name: string;
    status: string;
    token_expires_at?: string | null;
  }>;
  queue: { draft: number; scheduled: number; failed: number };
  upcoming: Array<Record<string, unknown>>;
  recent_failed: Array<Record<string, unknown>>;
}

/** Flache Kennzahlen für Dashboard-Strips. */
export interface CommandCenterSummary {
  accounts_active: number;
  accounts_expired: number;
  scheduled_next_7_days: number;
  failed_targets: number;
  upcoming: Array<{
    id: string;
    post_id: string;
    platform: SocialPlatform;
    preview: string;
    scheduled_at: string;
  }>;
}

export const EMPTY_COMMAND_CENTER: CommandCenterSummary = {
  accounts_active: 0,
  accounts_expired: 0,
  scheduled_next_7_days: 0,
  failed_targets: 0,
  upcoming: [],
};

/** Rechnet die Rohdaten des Command-Center-Endpunkts in Kennzahlen um. */
export function summarizeCommandCenter(data: CommandCenterData): CommandCenterSummary {
  const accounts = data.accounts ?? [];
  return {
    accounts_active: accounts.filter((a) => a.status === 'active').length,
    accounts_expired: accounts.filter((a) => a.status === 'expired' || a.status === 'error').length,
    scheduled_next_7_days: data.queue?.scheduled ?? 0,
    failed_targets: data.queue?.failed ?? 0,
    upcoming: (data.upcoming ?? []).map((row) => {
      const post = (row.social_posts ?? {}) as { title?: string; body?: string };
      return {
        id: String(row.id ?? ''),
        post_id: String(row.post_id ?? ''),
        platform: (row.platform as SocialPlatform) ?? 'linkedin',
        preview: (post.title || post.body || 'Ohne Text').slice(0, 80),
        scheduled_at: String(row.scheduled_at ?? ''),
      };
    }),
  };
}

export interface TargetInput {
  social_account_id: string;
  platform: SocialPlatform;
  post_kind?: SocialPostKind;
  adapted_body?: string | null;
  adapted_title?: string | null;
  privacy_status?: string | null;
  scheduled_at?: string | null;
  metadata?: SocialTargetMetadata;
}

export interface PostInput {
  body?: string | null;
  title?: string | null;
  post_kind: SocialPostKind;
  link_url?: string | null;
  first_comment?: string | null;
  campaign_id?: string | null;
  media_asset_ids?: string[];
  targets: TargetInput[];
  planned_at?: string | null;
  pillar_id?: string | null;
  owner_id?: string | null;
  board_status?: BoardStatus;
  notes?: string | null;
  tags?: string[];
  newsletter_enabled?: boolean;
  newsletter_list_id?: string | null;
  newsletter_subject?: string | null;
  newsletter_from_name?: string | null;
  newsletter_reply_to?: string | null;
}

/** Wirksame Server-Defaults für Planung und Link-Tracking. */
export interface SocialSettingsInfo {
  timezone: string;
  quiet_hours: { from: string; to: string } | null;
  require_approval: boolean;
  story_max_hours_ahead: number;
  utm_defaults: Record<string, string>;
  link_tracking: boolean;
  analytics_sync_enabled: boolean;
  queue_available: boolean;
}

export interface BestTimeSlot {
  weekday: number;
  hour: number;
  samples: number;
  avg_engagement: number;
  avg_impressions: number | null;
  score: number;
}

export interface BestTimeResult {
  platform: SocialPlatform | 'all';
  timezone: string;
  /** `data` = aus eigenen Beiträgen, `default` = Richtwerte. */
  source: 'data' | 'default';
  samples: number;
  slots: BestTimeSlot[];
}

export interface SlotSuggestion {
  slot_id: string | null;
  label: string;
  platform: SocialPlatform | null;
  post_kind: SocialPostKind | null;
  pillar_id: string | null;
  at: string;
  occupied: boolean;
}

export interface AnalyticsSummary {
  from: string;
  days: number;
  platforms: Array<{
    platform: SocialPlatform;
    posts: number;
    impressions: number;
    likes: number;
    comments: number;
    shares: number;
    clicks: number;
    engagement: number;
    engagement_rate: number | null;
  }>;
  totals: { posts: number; impressions: number; engagement: number };
  top_posts?: Array<{
    post_id: string;
    title: string;
    planned_at: string | null;
    impressions: number;
    engagement: number;
    platforms: SocialPlatform[];
  }>;
}

export interface SocialProviderStatus {
  platform: SocialPlatform;
  configured: boolean;
}

type Envelope<T> = { success: boolean; data?: T; error?: string; code?: string };

async function unwrap<T>(promise: Promise<Envelope<T>>): Promise<T> {
  const res = await promise;
  if (!res.success || res.data === undefined) {
    throw new Error(res.error || 'Anfrage fehlgeschlagen');
  }
  return res.data;
}

export const socialApi = {
  listAccounts: () => unwrap(api.get<Envelope<SocialAccount[]>>('/social/accounts')),

  disconnectAccount: async (id: string) => {
    const res = await api.delete<Envelope<unknown>>(`/social/accounts/${id}`);
    if (!res.success) throw new Error(res.error || 'Trennen fehlgeschlagen');
  },

  refreshAccount: (id: string) =>
    unwrap(api.post<Envelope<{ expires_at?: string }>>(`/social/accounts/${id}/refresh`)),

  accountHealth: (id: string) =>
    unwrap(
      api.get<Envelope<{ status: string; token_expires_at?: string | null; publishing_limit?: unknown }>>(
        `/social/accounts/${id}/health`
      )
    ),

  listProviders: () =>
    unwrap(api.get<Envelope<SocialProviderStatus[]>>('/social/oauth/providers')),

  getAuthorizeUrl: async (
    platform: SocialPlatform,
    opts?: { type?: 'personal' | 'company'; returnTo?: string }
  ) => {
    const qs = new URLSearchParams();
    if (opts?.type) qs.set('type', opts.type);
    if (opts?.returnTo) qs.set('return_to', opts.returnTo);
    qs.set('format', 'json');
    const query = qs.toString();
    const res = await api.get<Envelope<{ url: string }>>(
      `/social/oauth/${platform}/authorize${query ? `?${query}` : ''}`
    );
    if (!res.success || !res.data?.url) throw new Error(res.error || 'OAuth URL fehlt');
    return res.data;
  },

  listPosts: async (opts?: {
    all?: boolean;
    status?: string;
    platform?: string;
    campaign_id?: string;
    board_status?: BoardStatus;
    pillar_id?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (opts?.status) q.set('status', opts.status);
    if (opts?.platform) q.set('platform', opts.platform);
    if (opts?.campaign_id) q.set('campaign_id', opts.campaign_id);
    if (opts?.board_status) q.set('board_status', opts.board_status);
    if (opts?.pillar_id) q.set('pillar_id', opts.pillar_id);
    if (opts?.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    const path = `/social/posts${qs ? `?${qs}` : ''}`;
    return opts?.all ? getAllPages<SocialPost>(path) : unwrap(api.get<Envelope<SocialPost[]>>(path));
  },

  getPost: (id: string) => unwrap(api.get<Envelope<SocialPost>>(`/social/posts/${id}`)),

  createPost: (payload: PostInput) => unwrap(api.post<Envelope<SocialPost>>('/social/posts', payload)),

  updatePost: (id: string, payload: Partial<PostInput> & { status?: string }) =>
    unwrap(api.patch<Envelope<SocialPost>>(`/social/posts/${id}`, payload)),

  deletePost: (id: string) =>
    api.delete<Envelope<unknown>>(`/social/posts/${id}`).then((res) => {
      if (!res.success) throw new Error(res.error || 'Löschen fehlgeschlagen');
    }),

  schedulePost: (id: string, scheduled_at: string) =>
    unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/schedule`, { scheduled_at })),

  publishPost: (id: string) => unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/publish`)),

  approvePost: (id: string, expectedUpdatedAt: string) => unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/approve`, { expected_updated_at: expectedUpdatedAt })),

  rejectPost: (id: string) => unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/reject`)),

  newsletterLists: () =>
    unwrap(api.get<Envelope<Array<{ id: string; name: string; member_count: number }>>>('/social/newsletter/lists')),

  newsletterCalendar: () =>
    unwrap(
      api.get<Envelope<Array<{ id: string; title: string; sent_at: string | null; status: string }>>>(
        '/social/newsletter/calendar'
      )
    ),

  duplicatePost: (id: string) => unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/duplicate`)),

  /**
   * Returns the envelope rather than just `data`, because `meta` reports how many
   * targets the AI actually rewrote — without it a truncation-only fallback is
   * indistinguishable from a real adaptation.
   */
  adaptPost: async (id: string, opts?: { target_id?: string; platform?: SocialPlatform }) => {
    const res = await api.post<
      Envelope<SocialPost> & {
        meta?: { ai_applied_count: number; total: number; ai_unavailable_reason: string | null };
      }
    >(`/social/posts/${id}/adapt`, opts ?? {});
    if (!res.success || res.data === undefined) {
      throw new Error(res.error || 'Anpassen fehlgeschlagen');
    }
    return { post: res.data, meta: res.meta };
  },

  draftText: (payload: {
    brief: string;
    platforms?: SocialPlatform[];
    kind?: SocialPostKind;
    tone?: string;
  }) => unwrap(api.post<Envelope<{ body: string }>>('/social/ai/draft', payload)),

  improveText: (payload: { text: string; instruction?: string }) =>
    unwrap(api.post<Envelope<{ body: string }>>('/social/ai/improve', payload)),

  postAnalytics: (id: string) =>
    unwrap(api.get<Envelope<Array<{ target_id: string; analytics?: Record<string, number> }>>>(
      `/social/posts/${id}/analytics`
    )),

  // ── Targets ──────────────────────────────────────────────────────────────
  updateTarget: (id: string, payload: Partial<Omit<TargetInput, 'social_account_id' | 'platform'>>) =>
    unwrap(api.patch<Envelope<SocialPostTarget>>(`/social/targets/${id}`, payload)),

  publishTarget: (id: string) =>
    unwrap(api.post<Envelope<{ job_id?: string }>>(`/social/targets/${id}/publish`)),

  retryTarget: (id: string) => unwrap(api.post<Envelope<SocialPostTarget>>(`/social/targets/${id}/retry`)),

  retryAllFailed: () =>
    unwrap(api.post<Envelope<{ queued: number }>>('/social/targets/retry-failed')),

  cancelTarget: (id: string) => unwrap(api.post<Envelope<SocialPostTarget>>(`/social/targets/${id}/cancel`)),

  // ── Planner ──────────────────────────────────────────────────────────────
  planner: (from: string, to: string) => {
    const q = new URLSearchParams({ from, to });
    return unwrap(api.get<Envelope<PlannerData>>(`/social/planner?${q.toString()}`));
  },

  movePlannerPost: (
    id: string,
    payload: { planned_at?: string | null; board_status?: BoardStatus; pillar_id?: string | null; owner_id?: string | null }
  ) => unwrap(api.patch<Envelope<SocialPost>>(`/social/planner/posts/${id}`, payload)),

  listPillars: () => unwrap(api.get<Envelope<ContentPillar[]>>('/social/pillars')),

  createPillar: (payload: { name: string; color?: string; description?: string; sort_order?: number }) =>
    unwrap(api.post<Envelope<ContentPillar>>('/social/pillars', payload)),

  updatePillar: (id: string, payload: Partial<ContentPillar>) =>
    unwrap(api.patch<Envelope<ContentPillar>>(`/social/pillars/${id}`, payload)),

  deletePillar: (id: string) =>
    api.delete<Envelope<unknown>>(`/social/pillars/${id}`).then((res) => {
      if (!res.success) throw new Error(res.error || 'Löschen fehlgeschlagen');
    }),

  listSlots: () => unwrap(api.get<Envelope<ContentSlot[]>>('/social/slots')),

  createSlot: (payload: Omit<ContentSlot, 'id' | 'is_active'> & { is_active?: boolean }) =>
    unwrap(api.post<Envelope<ContentSlot>>('/social/slots', payload)),

  updateSlot: (id: string, payload: Partial<ContentSlot>) =>
    unwrap(api.patch<Envelope<ContentSlot>>(`/social/slots/${id}`, payload)),

  deleteSlot: (id: string) =>
    api.delete<Envelope<unknown>>(`/social/slots/${id}`).then((res) => {
      if (!res.success) throw new Error(res.error || 'Löschen fehlgeschlagen');
    }),

  // ── Media ────────────────────────────────────────────────────────────────
  listMedia: () => getAllPages<MediaAsset>('/social/media'),

  uploadMedia: async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    // Measured client-side: the API cannot inspect media, and without these the
    // Reel/Short/Story rules can only fail at the provider.
    const probed = await probeMedia(file);
    if (probed.width) fd.append('width', String(probed.width));
    if (probed.height) fd.append('height', String(probed.height));
    if (probed.durationSeconds) fd.append('duration_seconds', String(probed.durationSeconds));
    return unwrap(api.upload<Envelope<MediaAsset>>('/social/media/upload', fd));
  },

  deleteMedia: (id: string) =>
    api.delete<Envelope<unknown>>(`/social/media/${id}`).then((res) => {
      if (!res.success) throw new Error(res.error || 'Löschen fehlgeschlagen');
    }),

  commandCenter: () => unwrap(api.get<Envelope<CommandCenterData>>('/social/command-center')),

  // ── Settings, Insights & Betrieb ─────────────────────────────────────────
  settings: () => unwrap(api.get<Envelope<SocialSettingsInfo>>('/social/settings')),

  linkPreview: (url: string, platform?: SocialPlatform) =>
    unwrap(
      api.post<Envelope<{ original: string; tracked: string; tracking_enabled: boolean }>>(
        '/social/links/preview',
        { url, platform }
      )
    ),

  bestTimes: (opts?: { platform?: SocialPlatform; days?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (opts?.platform) q.set('platform', opts.platform);
    if (opts?.days) q.set('days', String(opts.days));
    if (opts?.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return unwrap(api.get<Envelope<BestTimeResult>>(`/social/insights/best-times${qs ? `?${qs}` : ''}`));
  },

  nextSlots: (opts?: { platform?: SocialPlatform; count?: number; includeOccupied?: boolean }) => {
    const q = new URLSearchParams();
    if (opts?.platform) q.set('platform', opts.platform);
    if (opts?.count) q.set('count', String(opts.count));
    if (opts?.includeOccupied) q.set('include_occupied', 'true');
    const qs = q.toString();
    return unwrap(api.get<Envelope<SlotSuggestion[]>>(`/social/slots/next${qs ? `?${qs}` : ''}`));
  },

  scheduleNextSlot: (id: string) =>
    unwrap(api.post<Envelope<SocialPost>>(`/social/posts/${id}/schedule-next-slot`)),

  analyticsSummary: (days = 30) =>
    unwrap(api.get<Envelope<AnalyticsSummary>>(`/social/analytics/summary?days=${days}`)),

  analyticsHistory: (postId: string) =>
    unwrap(
      api.get<Envelope<Array<Record<string, unknown>>>>(`/social/posts/${postId}/analytics/history`)
    ),

  reconcileQueue: () =>
    unwrap(
      api.post<Envelope<{ requeued: number; rescheduled: number; stuck: number }>>(
        '/social/queue/reconcile'
      )
    ),

  syncAnalytics: () =>
    unwrap(
      api.post<Envelope<{ checked: number; updated: number; failed: number }>>('/social/analytics/sync')
    ),
};
